/**
 * Builds the config that wires `@tradeparadigm/terminal-react` into Paradigm
 * via the DIME cloud flow: `DimeCloudApp` owns sign-in (Clerk) and derives
 * the session identity fields (`accountId` / `user` / `onLogout`) itself, and
 * the cloud layer supplies the connection fields (gateway grant, sidecar
 * auth, handshake tokens). This host contributes only presentation and
 * prompt framing.
 */
import { logException } from '@paradigm/logging/src/logging';
import type {
  CloudAppBaseConfig,
  DimeEnvironment,
} from '@tradeparadigm/terminal-react';

import { PARADIGM_BLOCK_TRADE_PREAMBLE } from './block-trade-preamble';
import { PARADIGM_TERMINAL_NAMESPACE } from './agent-url';

declare const ENVIRONMENT: string | undefined;

/** Client-id prefix shown in the gateway's pairing-approval prompt. */
export const PARADIGM_BLOCK_TERMINAL_CLIENT_ID_PREFIX =
  'paradigm-block-terminal';

/**
 * DIME cloud backend for this build. DIME has exactly two backends
 * (`production` | `testnet`), so production builds target production and
 * every other Paradigm env (test/dev/stage/local) targets testnet.
 */
export const PARADIGM_DIME_ENVIRONMENT: DimeEnvironment =
  typeof ENVIRONMENT !== 'undefined' && ENVIRONMENT === 'production'
    ? 'production'
    : 'testnet';

/** The primary Dime Terminal app for this backend — where sign-in happens. */
const DIME_PRIMARY_APP_URL =
  PARADIGM_DIME_ENVIRONMENT === 'production'
    ? 'https://app.dimeterminal.ai'
    : 'https://app.testnet.dimeterminal.ai';

/**
 * Clerk satellite config for `DimeTerminalProvider` (same shape Paradex
 * uses): instead of mounting Clerk's embedded email sign-in, the terminal
 * redirects to the primary Dime Terminal app to sign in, which then bounces
 * back to this satellite domain with the session.
 */
export const PARADIGM_DIME_CLERK = {
  isSatellite: true,
  domain: 'paradigm.trade',
  signInUrl: DIME_PRIMARY_APP_URL,
  signUpUrl: DIME_PRIMARY_APP_URL,
} as const;

/**
 * Host terminal config for the signed-in DIME user. Static because
 * everything session-scoped is derived by `DimeCloudApp` from the Clerk
 * session; connection fields are filled in by the cloud layer.
 */
export const PARADIGM_CLOUD_TERMINAL_CONFIG: CloudAppBaseConfig = {
  namespace: PARADIGM_TERMINAL_NAMESPACE,

  clientIdPrefix: PARADIGM_BLOCK_TERMINAL_CLIENT_ID_PREFIX,
  // Desktop's historical connection posture: bounded reconnect attempts via
  // circuit breaker (vs Paradex's heartbeat), and no pairing-role fallback —
  // the cloud grant is the only credential source.
  healthStrategy: 'circuit-breaker',
  supportsPairingRole: false,

  systemPreamble: PARADIGM_BLOCK_TRADE_PREAMBLE,

  logException: (error: unknown) => logException('dime-terminal', error),

  features: { cron: true },
};
